import { getParsedUser, getAuthToken } from './authStorage';

// Utility to track and save carts to backend

// Normalize URL to remove trailing slashes
const normalizeUrl = (url) => {
  if (!url) return '';
  return url.replace(/\/+$/, '');
};

const API_BASE_URL = normalizeUrl(process.env.REACT_APP_API_URL);

const getSessionId = () => {
  let sessionId = localStorage.getItem('raheasy_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('raheasy_session_id', sessionId);
  }
  return sessionId;
};

const getUserId = () => {
  const user = getParsedUser();
  if (!user) return null;
  return user.id || user._id || user.userId || null;
};

export const saveCartToBackend = async (items, customerInfo = null) => {
  try {
    const sessionId = getSessionId();
    const userId = getUserId();

    // Transform cart items to match backend schema
    const cartItems = items.map(item => ({
      product: item.id, // In production, this should be the product ObjectId
      quantity: item.quantity,
      price: item.price
    }));

    const response = await fetch(`${API_BASE_URL ? `${API_BASE_URL}/api` : ''}/carts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        sessionId,
        userId: userId || undefined,
        items: cartItems,
        customerInfo: customerInfo || undefined
      })
    });

    if (!response.ok) {
      console.error('Failed to save cart:', response.statusText);
    }
  } catch (error) {
    console.error('Error saving cart:', error);
    // Don't throw - cart saving should not block user experience
  }
};

export const updateCartCustomerInfo = async (customerInfo) => {
  try {
    const sessionId = getSessionId();
    const userId = getUserId();

    // Get current cart items from localStorage
    const cartItems = JSON.parse(localStorage.getItem('raheasy_cart') || '[]');
    
    await saveCartToBackend(cartItems, customerInfo);

    // Create or update lead if email or phone is provided
    if (customerInfo.email || customerInfo.phone) {
      try {
        await fetch(`${API_BASE_URL ? `${API_BASE_URL}/api` : ''}/customers/leads`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: customerInfo.email || `guest_${Date.now()}@raheasy.co.ke`,
            name: customerInfo.name || '',
            phone: customerInfo.phone || '',
            source: 'website',
            status: 'new',
            notes: 'Customer info from checkout'
          })
        });
      } catch (leadError) {
        console.error('Error creating lead:', leadError);
        // Don't throw - lead creation should not block checkout
      }
    }
  } catch (error) {
    console.error('Error updating cart customer info:', error);
  }
};

// Load user's cart from backend
export const loadUserCart = async () => {
  try {
    const userId = getUserId();
    if (!userId) return null;

    const API_BASE_URL = normalizeUrl(process.env.REACT_APP_API_URL);
    const token = getAuthToken();
    
    const response = await fetch(`${API_BASE_URL ? `${API_BASE_URL}/api` : ''}/carts/my-cart`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    });

    if (response.ok) {
      const cart = await response.json();
      if (cart && cart.items && cart.items.length > 0) {
        // Transform backend cart items to frontend format
        const frontendItems = cart.items.map(item => {
          const product = item.product;
          // Get image with priority: thumbnail > primary image > first image
          let image = '/images/default-product.png';
          if (product?.thumbnail) {
            image = product.thumbnail;
          } else if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
            const primaryIndex = product.primaryImageIndex !== undefined && product.primaryImageIndex < product.images.length
              ? product.primaryImageIndex
              : 0;
            image = product.images[primaryIndex];
          } else if (product?.image) {
            image = product.image;
          }
          
          return {
            id: product?._id || item.product,
            _id: product?._id,
            name: product?.name || 'Product',
            price: item.price || product?.price || 0,
            quantity: item.quantity,
            image: image,
            thumbnail: product?.thumbnail,
            images: product?.images,
            imageFallbacks: product?.imageFallbacks,
            imagekitImages: product?.imagekitImages,
            thumbnailFallback: product?.thumbnailFallback,
            thumbnailImagekit: product?.thumbnailImagekit,
            primaryImageIndex: product?.primaryImageIndex
          };
        });
        return frontendItems;
      }
    }
    return null;
  } catch (error) {
    console.error('Error loading user cart:', error);
    return null;
  }
};

// Debounce function to avoid too many API calls
let saveCartTimeout;
export const debouncedSaveCart = (items, customerInfo = null) => {
  clearTimeout(saveCartTimeout);
  saveCartTimeout = setTimeout(() => {
    saveCartToBackend(items, customerInfo);
  }, 2000); // Save after 2 seconds of inactivity
};

